import { AcceptedFiles } from '@insurancemenu/imenu-components-lib/dist/types/FileUpload';
import { DefaultFormFields, FormControl, FormController } from '@insurancemenu/imenu-components-lib/dist/utils/forms';
import { uploadRFQResponseDocument } from 'api/operations/documentsService';
import { uploadFiles } from 'global/utils/uploadFiles';
import { makeAutoObservable, runInAction } from 'mobx';

interface ContextualProps {
  entityId: string;
  onSubmit: (entityId: string) => Promise<void>;
  setPageIsLoading: (status: boolean) => void;
  getExistingDocumentTypes: () => string[];
}

interface UploadDocFields extends DefaultFormFields {
  docName: FormControl<string>;
  description: FormControl<string>;
}

export class UploadResponseDocumentModalStore {
  private readonly _entityId: string;
  private readonly _setPageIsLoading;
  private readonly _onSubmit;
  private readonly _getExistingDocumentTypes: () => string[];

  private _uploadedFile: AcceptedFiles | null = null;
  private _isLoading = false;

  public form = new FormController<UploadDocFields>({
    docName: new FormControl('', {
      validations: ['printableCharacters'],
      customValidations: [
        {
          check: this._nameIsUnique.bind(this),
          msg: 'Document with the same name already exists.',
        },
      ],
      optional: false,
      trim: true,
    }),
    description: new FormControl('', { validations: ['printableCharacters'], optional: true, trim: true }),
  });

  get isDataValid(): boolean {
    return (
      this.form.basicFieldsValidationsPassed &&
      !(this._uploadedFile === null || Object.keys(this._uploadedFile).length < 1)
    );
  }

  get isLoading(): boolean {
    return this._isLoading;
  }

  public setFile(files: AcceptedFiles): void {
    this._uploadedFile = files;
  }

  public resetModal(): void {
    this.form.resetForm();
    this._uploadedFile = null;
  }

  private _nameIsUnique(value: string): boolean {
    const existingDocs = this._getExistingDocumentTypes();
    return !existingDocs.find((docType) => docType.toLowerCase() === value.toLowerCase().trim());
  }

  public async requestUploadDocument(): Promise<void> {
    if (!this._uploadedFile) {
      throw new Error('No file uploaded.');
    }
    this._setPageIsLoading(true);
    const uploadedFileName = Object.keys(this._uploadedFile)[0];

    // Get upload url
    const uploadUrl = await uploadRFQResponseDocument(
      this._entityId,
      this.form.fields.docName.value.trim(),
      uploadedFileName,
      this.form.fields.description.value.trim(),
    );

    await runInAction(async () => {
      if (!this._uploadedFile) {
        throw new Error('No file uploaded.');
      }
      // Upload file to bucket
      await uploadFiles(this._uploadedFile, { [uploadedFileName]: uploadUrl });
      await runInAction(async () => {
        await this._onSubmit(this._entityId);
        runInAction(() => {
          this.resetModal();
          this._setPageIsLoading(false);
        });
      });
    });
  }

  constructor({ entityId, onSubmit, setPageIsLoading, getExistingDocumentTypes }: ContextualProps) {
    this._entityId = entityId;
    this._onSubmit = onSubmit;
    this._setPageIsLoading = setPageIsLoading;
    this._getExistingDocumentTypes = getExistingDocumentTypes;

    makeAutoObservable(this, {}, { autoBind: true });
  }
}
